import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, Trash2, X, Undo2 } from "lucide-react";

const CONFIRM_PHRASE = "RESET";
const UNDO_SECONDS = 60;

/** Sonner custom-toast body — shows a 60s countdown + Undo button so the
 *  admin has a parachute after any accidental wipe. Managed as its own
 *  functional component so the countdown interval lives inside a React
 *  lifecycle (not a stale closure inside DangerZoneCard). */
const UndoResetToast = ({ toastId, snapshotId, wipedSummary }) => {
  const [remaining, setRemaining] = useState(UNDO_SECONDS);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (remaining <= 0) {
      // Countdown expired — dismiss quietly and refresh so the (still-wiped)
      // state is what the user sees on every page.
      toast.dismiss(toastId);
      window.setTimeout(() => window.location.reload(), 400);
      return;
    }
    const t = window.setTimeout(() => setRemaining((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [remaining, toastId]);

  const undo = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post("/admin/reset-restore", {
        snapshot_id: snapshotId,
      });
      const line = Object.entries(data.restored || {})
        .map(([k, v]) => `${k}: ${v}`)
        .join(" · ");
      toast.dismiss(toastId);
      toast.success(`Restored — ${line || "nothing to restore"}`);
      window.setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Restore failed — snapshot may have expired",
      );
      setRestoring(false);
    }
  };

  const line = Object.entries(wipedSummary || {})
    .map(([k, v]) => `${v} ${k}`)
    .join(" · ");

  return (
    <div
      className="nb-card p-4 bg-white border-[#E5263F] flex items-center gap-3"
      style={{ borderColor: "#E5263F", minWidth: 380 }}
      data-testid="undo-reset-toast"
    >
      <div className="flex-1">
        <div className="font-display text-base flex items-center gap-2">
          <Trash2 size={16} strokeWidth={2.5} className="text-[#E5263F]" />
          Wiped {line || "nothing"}
        </div>
        <div className="text-xs text-[#6B6B6B]">
          Auto-refresh in {remaining}s — hit Undo to restore.
        </div>
      </div>
      <button
        type="button"
        onClick={undo}
        disabled={restoring}
        data-testid="undo-reset-btn"
        className="nb-btn nb-btn-primary px-3 py-2 flex items-center gap-1.5 text-sm disabled:opacity-60"
      >
        <Undo2 size={14} strokeWidth={2.5} />
        {restoring ? "Restoring…" : `Undo (${remaining}s)`}
      </button>
    </div>
  );
};

/** Single wipe-target checkbox row. Extracted out of DangerZoneCard so
 *  React doesn't rebuild the subtree on every keystroke in the confirm
 *  field (avoids the react/no-unstable-nested-components warning). */
const WipeRow = ({ testid, checked, onChange, title, subtitle, tone = "", count }) => (
  <label
    className={`flex items-start gap-2 nb-card-sm p-2 cursor-pointer ${
      checked ? tone : ""
    }`}
  >
    <input
      data-testid={testid}
      type="checkbox"
      className="w-5 h-5 mt-0.5"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="text-sm flex-1">
      <span className="font-bold flex items-center justify-between gap-2">
        <span>{title}</span>
        {typeof count === "number" && (
          <span
            data-testid={`${testid}-count`}
            className={`font-mono-num text-xs px-2 py-0.5 rounded-md border-2 border-black ${
              count > 0 ? "bg-[#FFE9C2] text-[#7A4A00]" : "bg-white text-[#6B6B6B]"
            }`}
          >
            {count.toLocaleString()}
          </span>
        )}
      </span>
      {subtitle && (
        <span className="block text-xs text-[#6B6B6B]">{subtitle}</span>
      )}
    </span>
  </label>
);

/**
 * Manager-only "Danger Zone" — wipes operational data so the warehouse can
 * start clean. Every collection is opt-in via a checkbox so managers can
 * clear test entries (movements + POs) without touching real inventory.
 *
 * The destructive action requires typing CONFIRM_PHRASE exactly.
 */
const DangerZoneCard = () => {
  const { user, isManager } = useAuth();
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  // Sensible go-live defaults: clear transactional history, keep the catalog.
  const [wipeStock, setWipeStock] = useState(false);
  const [wipeMovements, setWipeMovements] = useState(true);
  const [wipePOs, setWipePOs] = useState(true);
  const [wipeBugs, setWipeBugs] = useState(true);
  const [wipeSuppliers, setWipeSuppliers] = useState(false);
  const [wipeUsers, setWipeUsers] = useState(false);
  const [wipeDepartments, setWipeDepartments] = useState(false);
  const [wipeStockTypes, setWipeStockTypes] = useState(false);
  const [busy, setBusy] = useState(false);
  // Populated when the dialog opens — feeds the "will wipe N rows" preview
  // next to each checkbox. null while loading; {} on error.
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCounts(null);
    api
      .get("/admin/reset-preview")
      .then((r) => setCounts(r.data || {}))
      .catch(() => setCounts({}));
  }, [open]);

  if (!isManager) return null;

  const anySelected =
    wipeStock ||
    wipeMovements ||
    wipePOs ||
    wipeBugs ||
    wipeSuppliers ||
    wipeUsers ||
    wipeDepartments ||
    wipeStockTypes;

  const reset = () => {
    setPhrase("");
    setWipeStock(false);
    setWipeMovements(true);
    setWipePOs(true);
    setWipeBugs(true);
    setWipeSuppliers(false);
    setWipeUsers(false);
    setWipeDepartments(false);
    setWipeStockTypes(false);
  };

  const run = async () => {
    if (phrase.trim().toUpperCase() !== CONFIRM_PHRASE) {
      toast.error(`Type ${CONFIRM_PHRASE} to confirm`);
      return;
    }
    if (!anySelected) {
      toast.error("Tick at least one collection to wipe");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", {
        confirm: CONFIRM_PHRASE,
        wipe_stock: wipeStock,
        wipe_movements: wipeMovements,
        wipe_purchase_orders: wipePOs,
        wipe_bug_reports: wipeBugs,
        wipe_suppliers: wipeSuppliers,
        wipe_users: wipeUsers,
        wipe_departments: wipeDepartments,
        wipe_stock_types: wipeStockTypes,
      });
      const lines = Object.entries(data.wiped || {}).map(
        ([k, v]) => `${k}: ${v}`,
      );
      if (data.snapshot_id) {
        // Persistent Undo toast (dismisses itself + refreshes after 60s).
        toast.custom(
          (t) => (
            <UndoResetToast
              toastId={t}
              snapshotId={data.snapshot_id}
              wipedSummary={data.wiped}
            />
          ),
          { duration: Infinity },
        );
      } else {
        toast.success(
          `Reset complete — ${lines.join(" · ") || "nothing wiped"}`,
        );
        window.setTimeout(() => window.location.reload(), 800);
      }
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const Row = null; // deprecated inline — WipeRow is used instead (see top of file).
  void Row;

  return (
    <>
      <div
        className="nb-card p-5 mt-6 border-[#E5263F]"
        data-testid="danger-zone-card"
        style={{ borderColor: "#E5263F" }}
      >
        <div className="flex items-center gap-2 mb-2 text-[#E5263F]">
          <AlertTriangle size={20} strokeWidth={2.5} />
          <h3 className="font-display text-xl">Danger zone</h3>
        </div>
        <p className="text-sm text-[#404040] mb-3">
          Wipe test/pilot data. Tick just the collections you want cleared —
          e.g. clear <b>movements</b> and <b>POs</b> only, while keeping your
          real stock catalog intact. This action cannot be undone.
        </p>
        <button
          data-testid="open-reset-data-btn"
          onClick={() => setOpen(true)}
          className="nb-btn nb-btn-danger px-4 py-2 flex items-center gap-2"
        >
          <Trash2 size={16} strokeWidth={2.5} /> Reset data…
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          data-testid="reset-data-dialog"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="nb-card w-[min(560px,94vw)] max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
              <div>
                <div className="label-eyebrow text-[#E5263F]">Danger zone</div>
                <h3 className="font-display text-2xl">Reset data</h3>
              </div>
              <button
                onClick={() => !busy && setOpen(false)}
                data-testid="reset-dialog-close"
                className="nb-btn bg-white p-2"
                disabled={busy}
              >
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-3">
              {counts && (
                <div
                  data-testid="reset-preview-summary"
                  className="nb-card-sm p-3 bg-[#FFF9EC] border-[#7A4A00] text-sm"
                >
                  <div className="font-display text-base mb-1">
                    Will wipe on confirm
                  </div>
                  <div className="text-xs text-[#6B6B6B]">
                    {[
                      wipeMovements && `${(counts.movements || 0).toLocaleString()} movements`,
                      wipePOs && `${(counts.purchase_orders || 0).toLocaleString()} POs`,
                      wipeBugs && `${(counts.bug_reports || 0).toLocaleString()} bug reports`,
                      wipeStock && `${(counts.stock || 0).toLocaleString()} stock items`,
                      wipeSuppliers && `${(counts.suppliers || 0).toLocaleString()} suppliers`,
                      wipeUsers && `${(counts.users || 0).toLocaleString()} other users`,
                      wipeDepartments && `${(counts.departments || 0).toLocaleString()} departments`,
                      wipeStockTypes && `${(counts.stock_types || 0).toLocaleString()} custom stock types`,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "nothing selected"}
                  </div>
                </div>
              )}
              {!counts && open && (
                <div className="text-xs text-[#6B6B6B] italic">Loading counts…</div>
              )}
              <div className="label-eyebrow">Transactional data</div>
              <WipeRow
                testid="reset-wipe-movements"
                checked={wipeMovements}
                onChange={setWipeMovements}
                title="Movements (Take Stock + Receive entries)"
                subtitle="Clears all IN/OUT history — current on-hand qty is NOT changed."
                tone="bg-[#FFF1F3]"
                count={counts?.movements}
              />
              <WipeRow
                testid="reset-wipe-purchase-orders"
                checked={wipePOs}
                onChange={setWipePOs}
                title="Purchase Orders + PO counter"
                subtitle="Removes all POs and resets numbering to PO-0001."
                tone="bg-[#FFF1F3]"
                count={counts?.purchase_orders}
              />
              <WipeRow
                testid="reset-wipe-bug-reports"
                checked={wipeBugs}
                onChange={setWipeBugs}
                title="Bug reports"
                tone="bg-[#FFF1F3]"
                count={counts?.bug_reports}
              />

              <div className="label-eyebrow mt-1">Catalog & setup</div>
              <WipeRow
                testid="reset-wipe-stock"
                checked={wipeStock}
                onChange={setWipeStock}
                title="Stock items (catalog)"
                subtitle="⚠️ Wipes every product — leave OFF to keep your real inventory."
                tone="bg-[#FFD9DF]"
                count={counts?.stock}
              />
              <WipeRow
                testid="reset-wipe-suppliers"
                checked={wipeSuppliers}
                onChange={setWipeSuppliers}
                title="Suppliers"
                count={counts?.suppliers}
              />
              <WipeRow
                testid="reset-wipe-users"
                checked={wipeUsers}
                onChange={setWipeUsers}
                title="All other users"
                subtitle={`Keeps ${user?.name || "you"} only.`}
                count={counts?.users}
              />
              <WipeRow
                testid="reset-wipe-departments"
                checked={wipeDepartments}
                onChange={setWipeDepartments}
                title="Departments"
                subtitle="Default 4 seed on next boot."
                count={counts?.departments}
              />
              <WipeRow
                testid="reset-wipe-stock-types"
                checked={wipeStockTypes}
                onChange={setWipeStockTypes}
                title="Custom stock types"
                subtitle="Built-ins always remain."
                count={counts?.stock_types}
              />

              <label className="mt-1">
                <div className="label-eyebrow mb-1 text-[#E5263F]">
                  Type <span className="font-mono-num">RESET</span> to confirm
                </div>
                <input
                  data-testid="reset-confirm-input"
                  className="nb-input"
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder="RESET"
                  autoComplete="off"
                />
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  data-testid="reset-cancel-btn"
                  onClick={() => setOpen(false)}
                  className="nb-btn bg-white px-4 py-2 text-sm"
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  data-testid="reset-confirm-btn"
                  onClick={run}
                  disabled={
                    busy ||
                    !anySelected ||
                    phrase.trim().toUpperCase() !== CONFIRM_PHRASE
                  }
                  className="nb-btn nb-btn-danger px-4 py-2 text-sm flex items-center gap-1"
                >
                  <Trash2 size={14} strokeWidth={2.5} />
                  {busy ? "Resetting…" : "Wipe now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DangerZoneCard;
